import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const ENTRY_SCRIPT_PATTERN = /src="(\/dist\/main-[^"]+\.js)"/;

function getLoadedEntryScript(): string | undefined {
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  const entry = scripts.find((script) => /\/dist\/main-.*\.js$/.test(script.getAttribute('src') || ''));
  return entry?.getAttribute('src') ?? undefined;
}

async function getDeployedEntryScript(): Promise<string | undefined> {
  // index.html is served no-store, so this always reflects the current deployment.
  const response = await fetch(`/index.html?t=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) {
    return undefined;
  }

  return ENTRY_SCRIPT_PATTERN.exec(await response.text())?.[1];
}

/**
 * True once a newer build has been deployed than the one this tab is running. Compares the hashed
 * entry bundle rather than a version string, so it works without build metadata.
 */
export function useNewVersionAvailable(): boolean {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);

  useEffect(() => {
    const loaded = getLoadedEntryScript();
    if (!loaded) {
      return;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const deployed = await getDeployedEntryScript();
        if (!cancelled && deployed && deployed !== loaded) {
          setNewVersionAvailable(true);
        }
      } catch {
        // Offline or a blip; the next poll will pick it up.
      }
    };

    const intervalId = window.setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return newVersionAvailable;
}
